<template>
  <div class="flex flex-col w-full">
    <!-- Layout responsivo: una columna por defecto, dos columnas en md -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
      <!-- Columna izquierda -->
      <div class="flex flex-col space-y-4">

        <expandable-panel :item="_getConfigOptions('config_geolocation_center_point')"
          :mode="'mixed'"
          :option-type="'select'"
          :default-selected-value="selectedOption"
          :select-options="optionsSelect"
          @update:selectedValue="_updateSelectedOption"
        >
          <div class="text-sm text-gray-700">
            <b-message type="is-info">
              {{ $t('geolocation_enter_reference_point') }}
            </b-message>
            <h4>{{ $t('geolocation_pictogram_point') }}</h4>

            <div class="flex flex-row space-x-2">
              <div class="flex flex-col space-y-2">
                <label>{{ $t('x_meters') }}</label>
                <b-numberinput
                  v-model="geoConfigForm.localX"
                  size="is-small"
                  controls-position="compact"
                  step="0.01"
                  :disabled="selectedOption === 'none'"
                  icon-pack="fas"></b-numberinput>
              </div>
              <div class="flex flex-col space-y-2">
                <label>{{ $t('y_meters') }}</label>
                <b-numberinput
                  v-model="geoConfigForm.localY"
                  size="is-small"
                  controls-position="compact"
                  step="0.01"
                  :disabled="selectedOption === 'none'"
                  icon-pack="fas"></b-numberinput>
              </div>
            </div>

            <b-button
              size="is-small"
              type="is-primary"
              icon-pack="fas"
              icon-left="crosshairs"
              class="mt-2"
              :disabled="selectedOption === 'none'"
              @click="_usePictogramCenter"> {{ $t('geolocation_use_pictogram_center') }} </b-button>

            <h4 class="mt-4">{{ $t('geolocation_coordinates_gps') }}</h4>
            <div class="flex flex-row space-x-2">
              <div class="flex flex-col space-y-2">
                <label>{{ $t('latitude') }}</label>
                <b-numberinput
                  v-model="geoConfigForm.lat"
                  size="is-small"
                  controls-position="compact"
                  step="0.000001"
                  placeholder="-12.0464"
                  :disabled="selectedOption === 'none'"
                  icon-pack="fas"></b-numberinput>
              </div>
              <div class="flex flex-col space-y-2">
                <label>{{ $t('longitude') }}</label>
                <b-numberinput
                  v-model="geoConfigForm.lng"
                  placeholder="-77.0428"
                  size="is-small"
                  controls-position="compact"
                  step="0.000001"
                  :disabled="selectedOption === 'none'"
                  icon-pack="fas"></b-numberinput>
              </div>
            </div>

            <div class="flex flex-row space-x-2 mt-4">
              <b-button
                size="is-small"
                type="is-success"
                icon-pack="fas"
                icon-left="save"
                :disabled="!isGeoConfigValid || selectedOption === 'none'"
                @click="saveGeoReference">
                {{ $t('save') }}
              </b-button>
              <b-button
                v-if="geoReferenceConfigured && selectedOption !== 'none'"
                size="is-small"
                type="is-danger"
                icon-pack="fas"
                icon-left="trash"
                @click="clearGeoReference">
                {{ $t('clean') }}
              </b-button>
            </div>

            <b-message v-if="geoReferenceConfigured && selectedOption !== 'none'" type="is-success" class="mt-4">
              {{ $t('geolocation_configured_success') }}
            </b-message>
          </div>
        </expandable-panel>

      </div>

      <!-- Columna derecha -->
      <div class="flex flex-col space-y-4">
      </div>
    </div>
  </div>
</template>

<script>
import ExpandablePanel from '../layer-statuses/expandable-panel.vue';

import { getPictogramDesignGeoreferencing, updatePictogramDesignGeoreferencing } from '../../services/pictograms.service';

export default {
  name: 'PictogramGeolocationSettings',

  components: {
    ExpandablePanel,
  },

  props: {
    entity: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      selectedOption: 'none',

      pictogramId: this.entity.id || null,

      geoConfigForm: {
        localX: 0,
        localY: 0,
        lat: null,
        lng: null,
      },
      geoTransform: null,
      geoReferenceConfigured: false,

      pictogramDesign: null,
    }
  },

  computed: {
    optionsSelect() {
      return [
        { value: 'none', text: this.$t('geolocation_method_none') },
        { value: 'centerPoint', text: this.$t('geolocation_method_center_point') },
        // 'twoPoints' deshabilitado: método aún no implementado (evita elegir un método
        // que no guarda nada). Reactivar cuando se implemente.
      ]
    },

    isGeoConfigValid() {
      return this.geoConfigForm.lat !== null &&
              this.geoConfigForm.lng !== null &&
              this.geoConfigForm.lat >= -90 &&
              this.geoConfigForm.lat <= 90 &&
              this.geoConfigForm.lng >= -180 &&
              this.geoConfigForm.lng <= 180
    },
  },

  created() {
    this._loadDataDesign()
  },

  mounted() {
  },

  methods: {
    async _loadDataDesign() {
      try {
        const response = await getPictogramDesignGeoreferencing(this.pictogramId);

        if (response) {
          if (response.georeferencing && response.georeferencing.active) {
            if (response.georeferencing && response.georeferencing.method) {
              this.selectedOption = response.georeferencing.method === 'center-point' ? 'centerPoint' : 'none';
            }
          } else {
            this.selectedOption = 'none';
          }

          this.pictogramDesign = response;
          this.geoReferenceConfigured = false;

          if (this.selectedOption === 'centerPoint') {
              this._usePictogramCenter();
              this.geoConfigForm.lat = response.georeferencing.settings.centerPointMethod.location.coordinates[1];
              this.geoConfigForm.lng = response.georeferencing.settings.centerPointMethod.location.coordinates[0];
              this.geoReferenceConfigured = true;
          }
        }
      } catch (error) {
        console.error('Error al cargar la configuración del diseño del pictograma', error);
      }
    },

    /**
     * Guarda la geo-referencia del pictograma (persiste en el servidor vía API).
     */
    async saveGeoReference() {
      if (!this.isGeoConfigValid) {
        this.$notify.error('geolocation_fill_fields_correctly')
        return
      }

      if (!this.pictogramDesign) {
        this.$notify.error('geolocation_update_design_error');
        return;
      }

      const georeferencing = {
        active: this.selectedOption !== 'none',
        method: this.pictogramDesign.georeferencing.method,
        settings: JSON.parse(JSON.stringify(this.pictogramDesign.georeferencing.settings)),
      }


      if (this.selectedOption === 'centerPoint') {
        georeferencing.method = 'center-point';
        georeferencing.settings.centerPointMethod = {
          location: {
            type: 'Point',
            coordinates: [
              parseFloat(this.geoConfigForm.lng),
              parseFloat(this.geoConfigForm.lat),
            ],
          },
          anchorMode: 'center',
          rotation: 0,
          scale: 1.0,
        };

        georeferencing.settings.twoPointsMethod = null;
      }

      const payload = {
        georeferencing: georeferencing,
      }

      try {
        const response = await updatePictogramDesignGeoreferencing(this.pictogramId, payload);
        if (response.success) {
          this.$notify.success('geolocation_saved_successfully');
          this.$emit('epc-geo-reference-changed', true);
        } else {
          this.$notify.error('geolocation_update_design_error');
        }
      } catch (error) {
        console.error('Error al actualizar el diseño del pictograma', error);
        this.$notify.error('geolocation_update_design_error');
      }
    },

    /**
     * Elimina la geo-referencia del pictograma (persiste el borrado en el servidor).
     */
    async clearGeoReference() {
      // Reset del form local + método a 'none'.
      this.geoConfigForm = { localX: 0, localY: 0, lat: null, lng: null }
      this.selectedOption = 'none'
      this.geoReferenceConfigured = false

      if (!this.pictogramDesign) {
        this.$notify.error('geolocation_update_design_error');
        return;
      }

      const payload = {
        georeferencing: {
          active: false,
          method: 'none',
          settings: {
            centerPointMethod: null,
            twoPointsMethod: null,
          },
        },
      }

      try {
        const response = await updatePictogramDesignGeoreferencing(this.pictogramId, payload);
        if (response.success) {
          this.$notify.success('geolocation_cleared_successfully');
          this.$emit('epc-geo-reference-changed', true);
        } else {
          this.$notify.error('geolocation_update_design_error');
        }
      } catch (error) {
        console.error('Error al limpiar la geo-referencia del pictograma', error);
        this.$notify.error('geolocation_update_design_error');
      }
    },

    /**
     * Usa el centro del pictograma como punto de referencia local
     */
    _usePictogramCenter() {
      if (this.pictogramDesign) {
        const bounds = this.pictogramDesign.metadata.bounds;
        this.geoConfigForm.localX = (bounds.minX + bounds.maxX) / 2;
        this.geoConfigForm.localY = (bounds.minY + bounds.maxY) / 2;
      } else {
        this.$notify.warning('geolocation_pictogram_data_unavailable');
      }
    },

    _updateSelectedOption(value) {
      this.selectedOption = value;
    },

    _getConfigOptions(code) {
      const options = [
        {
          code: 'config_geolocation_center_point',
          name: this.$t('geolocation'),
          description: this.$t('geolocation_configure'),
          icon: 'map-marker-alt',
          order: 0,
          type: 'item',
        }
      ];

      return options.find(option => option.code === code) || null;
    },
  }
};
</script>

<style scoped>
h4 {
  font-weight: 600;
  margin-bottom: 0.5rem;
}
</style>
